import { useState } from 'react'

import { isAxiosError } from 'axios'
import { format } from 'date-fns'

import { useAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { FormattedMessage, showSnackbar } from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import {
  type ListingSearchState,
  useOptionalListingSearch,
} from '@/components/listings/search/ListingSearchProvider'
import { downloadFileFromUrl } from '@/helpers/api/downloadFileFromUrl'
import { fetchManifestExport } from '@/helpers/api/fetchManifestExport'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import {
  fetchAllFilteredListingIds,
  ManifestExportCapExceededError,
} from './data-loading/fetchAllFilteredListingIds'
import { trackDownloadAllManifestsClicked } from './helpers/listViewAnalytics'
import type { ListViewSurface } from './helpers/listViewSurface'

const getFileName = () =>
  `B-Stock listings ${format(new Date(), "yyyy-MM-dd'T'HHmm")}.csv`

/**
 * order-process's own EXPORT_CAP_EXCEEDED rejection — the authoritative
 * check. The client-side pre-check in
 * fetchAllFilteredListingIds is only a fail-fast optimization; the two caps
 * can drift (the backend's is independently env-tunable), so a request that
 * passes the client-side check can still be rejected here.
 */
const isBackendCapExceededError = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.data as { code?: string } | undefined)?.code ===
    'EXPORT_CAP_EXCEEDED'

export function useDownloadAllManifests({
  state,
  storefront,
  surface,
  urlPrefix,
}: {
  state: ListingSearchState
  storefront: StoreFrontDto | undefined
  surface?: ListViewSurface
  urlPrefix: string
}) {
  const accessToken = useAccessToken()
  // Keeps the export to the page's listings, e.g. First Dibs only.
  const scope = useOptionalListingSearch()?.scope
  const { track, trackButtonClicked, ...analytics } = useAnalytics()
  const [isPending, setIsPending] = useState(false)

  const downloadAllManifests = async () => {
    trackDownloadAllManifestsClicked({ trackButtonClicked }, analytics, surface)
    setIsPending(true)
    try {
      const { listingIds, total } = await fetchAllFilteredListingIds(
        accessToken,
        state,
        storefront,
        { scope }
      )

      if (!listingIds.length) {
        showSnackbar({
          content: (
            <FormattedMessage id="Listings.ListView.export.emptyResults" />
          ),
          options: { type: 'default' },
        })
        return
      }

      const { deepLink, excludedListingCount, truncatedListingCount } =
        await fetchManifestExport(accessToken, {
          listingIds,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          urlPrefix,
          storefrontSlug: storefront?.slug,
        })

      if (!deepLink) {
        throw new Error('getManifestExportAsync returned no deepLink')
      }

      await downloadFileFromUrl({
        fileName: getFileName(),
        url: deepLink,
        unwrapZip: true,
      })

      track('manifest_export_completed', {
        screenName: 'all_auction_list_view',
        ...surface,
        listing_count: total,
      })
      // Anything excluded (unaccepted seller terms) or truncated (a lot's manifest hit the
      // export's per-lot item ceiling) means the buyer didn't get everything they asked for —
      // that can't be reported as a plain, unqualified success.
      const isPartialExport = !!excludedListingCount || !!truncatedListingCount
      showSnackbar({
        content: (
          <FormattedMessage
            id="Listings.ListView.export.success"
            values={{
              excludedListingCount,
              truncatedListingCount,
            }}
          />
        ),
        options: { type: isPartialExport ? 'default' : 'success' },
      })
    } catch (error) {
      const isCapExceeded =
        error instanceof ManifestExportCapExceededError ||
        isBackendCapExceededError(error)
      track('manifest_export_failed', {
        screenName: 'all_auction_list_view',
        ...surface,
        reason: isCapExceeded ? 'cap_exceeded' : 'error',
      })
      showSnackbar({
        content: (
          <FormattedMessage
            id={
              isCapExceeded
                ? 'Listings.ListView.export.capExceeded'
                : 'Listings.ListView.export.error'
            }
          />
        ),
        options: { type: 'error' },
      })
    } finally {
      setIsPending(false)
    }
  }

  return { downloadAllManifests, isPending }
}
